import { Event } from "../../../../base/common/events";
import type { ProgramInfo, RenderStatsSummary } from "../../../../platform/webgl/common/renderStats";
import type { SceneTriangleCounts } from "../../../../platform/webgl/common/sceneStats";
import type { ResourceKind, ResourceKindCounts } from "../../../../platform/resources/common/resources.js";
import type { StripEvent } from "../../../../base/common/eventStrip.js";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import type { DeviceProfile, PerformanceRatings } from "./performanceRatings";

export type { DeviceProfile, PerformanceRatings, MetricRating } from "./performanceRatings";

export const IPerformanceService = createDecorator<IPerformanceService>("performanceService");

export interface PerformanceChangedEvent {

    memory?: true;

    fps?: true;

    resources?: true;

    render?: true;

    /** a new history sample was appended (fires ~1×/s) — for sparklines. */
    history?: true;

    /** the target device profile changed (ratings re-derive). */
    ratings?: true;
}

/** One compressible texture named in the savings breakdown. Bytes. */
export interface CompressibleTexture {
    readonly name: string;
    readonly saving: number;
    /** its VRAM today — the saving alone cannot say whether it is worth doing */
    readonly bytes?: number;
    readonly width?: number;
    readonly height?: number;
    /** the model file it was loaded from, when known */
    readonly model?: string;
    /** the texture's three uuid — lets the UI link it to a select/inspect action */
    readonly uuid?: string;
    /** a material (+slot) that samples it, when known — a second select target */
    readonly material?: string;
    readonly materialUuid?: string;
}

/** Estimated reclaimable VRAM, split by how you'd reclaim it. Bytes. */
export interface GpuMemorySavings {
    /** by GPU-compressing uncompressed textures (KTX2/basis) — an estimate */
    readonly compress: number;
    /** by deduping identical-content allocations loaded more than once */
    readonly duplicate: number;
    /** the heaviest compressible textures (names/models) — WHICH to compress */
    readonly textures: readonly CompressibleTexture[];
    /** by QUANTIZING mesh attributes and narrowing indices. Separate from
     *  `compress` because it is a different fix — and because Draco/meshopt shrink
     *  the DOWNLOAD without moving this number at all. */
    readonly geometry: number;
    /** the heaviest quantizable meshes — WHICH to re-encode */
    readonly geometries: readonly CompressibleGeometry[];
    /** by shipping FastHDR instead of an EXR/HDR environment. Apart from
     *  `compress` because basis cannot hold HDR, and because this one shrinks the
     *  download (~3x) as well as the VRAM (~21x). */
    readonly hdr: number;
}

/** One quantizable mesh named in the savings breakdown. Bytes. */
export interface CompressibleGeometry {
    readonly name: string;
    readonly saving: number;
    /** its VRAM today — a megabyte off 1.2 MB is most of it, off 40 MB it is noise */
    readonly bytes?: number;
    readonly verts?: number;
    readonly model?: string;
    readonly uuid?: string;
    /** attributes still stored as full floats */
    readonly quantizable?: readonly string[];
    /** no index buffer */
    readonly nonIndexed?: boolean;
    /** objects that draw it — how you find an unnamed geometry in the scene */
    readonly usedBy?: readonly string[];
}

/** One-per-second snapshot kept in the rolling history (for sparklines). */
export interface PerformanceSample {
    readonly at: number;
    readonly fps: number;
    /** used JS heap, bytes */
    readonly memory: number;
    /** GPU/VRAM total (textures + geometry), bytes */
    readonly gpuMemory: number;
    readonly drawCalls: number;
    /** rasterized triangles / frame */
    readonly triangles: number;
    /** in-scene mesh count (from the scene scan) */
    readonly meshes: number;
    /** loaded resource counts (alive) — the Assets "Loaded" column over time */
    readonly geometries: number;
    readonly materials: number;
    readonly textures: number;
    /** compiled shader programs — a STEP UP mid-session is a compile that just
     *  stalled a frame, which is why it is sampled rather than read once */
    readonly programs: number;
}

export interface IPerformanceService {

    readonly onDidChange: Event<PerformanceChangedEvent>;

    /**
     * Used memory in bytes
     */
    get usedMemory(): number | undefined;

    /**
     * Total memory allocated
     */
    get totalMemory(): number | undefined;

    /**
     * Frames per second
     */
    get fps(): number | undefined;

    /**
     * Total resources downloaded
     */
    get totalResourceDownload(): number | undefined;

    /** Largest network contributors from the same measurement as the total. */
    get resourceTransferBreakdown(): ResourceTransferBreakdown | undefined;

    /**
     * GPU/VRAM total of the tracked resources (textures + geometry, deduped by
     * allocation) in bytes, from the resource-analysis report. Undefined before the
     * first analysis pass or when no analysis service is available.
     */
    get gpuMemory(): number | undefined;

    /**
     * The renderers on the page and whether each drew recently.
     *
     * Every per-frame number here belongs to whichever renderer is drawing, and a
     * page can hold several (a viewer plus a thumbnail pass, WebGL beside WebGPU),
     * so a report that never names it leaves the reader guessing.
     */
    get renderers(): readonly { type: string; active: boolean; settings?: RendererSettings }[];

    /**
     * Estimated VRAM that could be RECLAIMED — by compressing uncompressed textures
     * and by deduping identical-content allocations. Undefined before the first
     * analysis pass. (Geometry savings may join later.)
     */
    get gpuMemorySavings(): GpuMemorySavings | undefined;

    /**
     * What the main renderer draws per displayed frame — draw calls, triangles and
     * render passes, averaged over the last second.
     *
     * Averaged because there is no real frame boundary to read: a postprocessed
     * scene runs several render() calls per displayed frame and nothing hooks the
     * host app rAF (see platform/webgl/common/renderStats.ts). Undefined when
     * nothing has rendered recently.
     */
    get renderStats(): RenderStatsSummary | undefined;

    /**
     * Triangles in the current scene: `visible` (what the camera would draw — the
     * live toolbar number) and `total` (incl. hidden — the tooltip). The scene's
     * geometry, NOT the GPU's per-frame work in {@link renderStats} (a mesh counts
     * once regardless of passes). Undefined when there is no scene.
     */
    get sceneTriangles(): SceneTriangleCounts | undefined;

    /** Rolling ~120s of one-per-second samples, OLDEST first — for sparklines. */
    get history(): readonly PerformanceSample[];

    /**
     * Timestamped performance events over roughly the same window (main-thread
     * jank + large resource loads), for correlating dips on the timeline with what
     * caused them. Captured passively via PerformanceObserver. Oldest first.
     */
    get events(): readonly StripEvent[];

    /**
     * Freeze the reported time-series ({@link events} + {@link history}) at their
     * current values so a PAUSED readout can't change or evict under a consumer —
     * the strip, the copy, and the AI/MCP report all see the same frozen window.
     * Recording continues into the live buffers underneath, so resume is seamless.
     */
    setPaused(paused: boolean): void;
    get paused(): boolean;

    /**
     * Live per-kind counts of tracked resources (the same source the Resources tab
     * and its badge use), or undefined when no resource service is available. Read
     * fresh each access, so consumers on the per-second change event stay current.
     */
    get resourceCounts(): ReadonlyMap<ResourceKind, ResourceKindCounts> | undefined;

    /**
     * Compiled shader programs (and their bytes where the backend reports them —
     * WebGPU does, WebGL does not). Undefined when no renderer reports it.
     */
    get programInfo(): ProgramInfo | undefined;

    /**
     * Device-aware good/warn/bad judgment per metric — computed in CORE, not the
     * UI, so the colour coding and the AI report agree. Thresholds derive from the
     * device (fps target from the observed peak refresh rate, memory from the heap
     * ceiling); a metric is absent when it can't be judged (e.g. no heap limit).
     */
    get ratings(): PerformanceRatings;

    /** The auto-detected device signals (what we could read about THIS machine). */
    get deviceProfile(): DeviceProfile;

    /**
     * The selected TARGET device (PRO) the ratings audit against, or null for
     * "auto" (rate against this machine). Set from a device-preset picker.
     */
    get targetProfile(): DeviceProfile | null;
    setTargetProfile(profile: DeviceProfile | null): void;

    /** The profile the ratings actually use: target merged over auto, or auto. */
    get effectiveProfile(): DeviceProfile;

    /** Target fps the rating uses — explicit target, else the observed peak/cadence. */
    get targetFps(): number;
}

export interface ResourceTransferBreakdown {
    readonly largestFiles: readonly { url: string; bytes: number; type: string }[];
    readonly largestTypes: readonly { type: string; bytes: number }[];
}
